import React, { useEffect, useState } from 'react';

import { boolToForm } from 'sellDirect/helpers/closingDash/formatters';
import { showToast } from '@opendoor/bricks/complex/Toast';
import {
  isServiceProvidersFormComplete,
  serviceProvidersFormToClosingTasks,
  isServiceProvidersFormValid,
} from 'sellDirect/helpers/closingDash/serviceProviders';
import {
  IResetableFormProps,
  IResetableFormRenderProps,
  ResetableForm,
} from 'sellDirect/components/closingDash/ResetableForm';
import { ServiceProvidersSummary } from 'sellDirect/components/closingDash/serviceProviders/ServiceProvidersSummary';
import { useUpdateClosingTasks } from 'sellDirect/graphql/useUpdateClosingTasks';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { Box } from '@opendoor/bricks/core';
import { ServiceProvidersFormContent } from 'sellDirect/components/closingDash/serviceProviders/ServiceProvidersFormContent';

export interface ServiceProvidersFormState {
  // utilities
  electricity?: string;
  gas?: string;
  hasGas?: string;
  water?: string;
  // other services
  additionalInfo?: string;
  mailboxNumber?: string;
  recyclingCollection?: string;
  trashCollection?: string;
}

export interface ServiceProvidersFormProps {
  shouldSeeAgentExperience: boolean;
  closingTasks: ClosingTasks;
  setInEditMode: (inEditMode: boolean) => void;
  onCompleted: () => void;
}

/**
 * Form for service providers.
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const ServiceProvidersForm: React.FC<ServiceProvidersFormProps> = ({
  shouldSeeAgentExperience,
  closingTasks,
  setInEditMode,
  onCompleted,
}) => {
  const serviceAccounts = closingTasks.serviceAccounts?.taskable;
  const otherServicesInfo = closingTasks?.otherHomeInfo?.taskable;
  const isFormComplete = isServiceProvidersFormComplete(closingTasks);

  const [formState, setFormState] = useState<ServiceProvidersFormState>({
    // utilities
    electricity: serviceAccounts?.electricity ?? undefined,
    gas: serviceAccounts?.gas ?? undefined,
    hasGas: boolToForm(serviceAccounts?.gas ? true : undefined, isFormComplete),
    water: serviceAccounts?.water ?? undefined,
    // other services
    additionalInfo: otherServicesInfo?.additionalInfo ?? undefined,
    mailboxNumber: otherServicesInfo?.mailboxNumber ?? undefined,
    recyclingCollection: otherServicesInfo?.recyclingCollection ?? undefined,
    trashCollection: otherServicesInfo?.trashCollection ?? undefined,
  });

  const [isEditing, setIsEditing] = useState(!isServiceProvidersFormValid(formState));
  const { updateClosingTasks, loading } = useUpdateClosingTasks('update-service-providers', {
    onCompleted: () => {
      setIsEditing(false);
      onCompleted();
    },
    onError: () => {
      setIsEditing(true);
      showToast(
        'error',
        'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
        'cake-update-service-providers-error-toast',
        { toastOptions: { position: 'bottom-left' } },
      );
    },
  });

  const onSubmit: IResetableFormProps['onSubmit'] = (resetableForm) => {
    setFormState(resetableForm?.formState);
    const tasks = serviceProvidersFormToClosingTasks(resetableForm?.formState);
    updateClosingTasks(tasks);
  };

  useEffect(() => {
    setInEditMode(isEditing);
  }, [setInEditMode, isEditing]);

  return (
    <Box mb={10}>
      {isEditing ? (
        <ResetableForm
          initialFormState={formState}
          onSubmit={onSubmit}
          render={(resetableForm: IResetableFormRenderProps) => (
            <ServiceProvidersFormContent
              shouldSeeAgentExperience={shouldSeeAgentExperience}
              closingTasks={closingTasks}
              resetableForm={resetableForm}
              onCancel={() => setIsEditing(false)}
              loading={loading}
            />
          )}
        />
      ) : (
        <ServiceProvidersSummary
          shouldSeeAgentExperience={shouldSeeAgentExperience}
          closingTasks={closingTasks}
          setIsEditingForm={setIsEditing}
        />
      )}
    </Box>
  );
};
